/**
 * IT review sub-flow:
 * Initiated → Security → Code & Arch → Development → Deployment → Release
 * Tracks stage transitions; past scan runs remain browsable.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, ChevronDown, ChevronRight, Download, ExternalLink, Loader2 } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import { encryptAccessTokenForTransport } from '../../../utils/secScanTokenCrypto';
import GrantSecScanAccessModal from '../modals/GrantSecScanAccessModal';
import ServiceAccountAccessSteps from './ServiceAccountAccessSteps';
import DeployedUrlModal from '../modals/DeployedUrlModal';
import GarageLoadingScreen from './GarageLoadingScreen';
import './ItScanFlowPanel.css';

const POLL_MS = 5000;
const ACTIVE_POLL_MS = 2500;
const ACTIVE = new Set(['pending', 'claimed', 'running', 'publishing']);

const NODE_ORDER = [
  { id: 'initiated', key: 'initiated', label: 'Initiated' },
  { id: 'security', key: 'security', label: 'Security scan' },
  { id: 'codeReview', key: 'code_review', label: 'Code & architecture' },
  { id: 'development', key: 'development', label: 'Development' },
  { id: 'deployment', key: 'deployment', label: 'Deployment' },
  { id: 'release', key: 'release', label: 'Release' },
];

const STATUS_LABELS = {
  pending: 'Queued',
  claimed: 'Starting',
  running: 'Scanning',
  publishing: 'Finalizing',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const VERDICT_LABELS = {
  GO: 'Ready to deploy',
  READY_WITH_FIXES: 'Ready with fixes',
  NOT_READY: 'Not ready',
};

const SUBFLOW_TO_NODE = {
  initiated: 'initiated',
  development: 'development',
  security: 'security',
  code_review: 'codeReview',
  deployment: 'deployment',
  release: 'release',
};

const nodeFromSubflow = (subflowNode) => SUBFLOW_TO_NODE[subflowNode] || 'initiated';

const REASON_LABELS = {
  registered: 'Registered',
  routed_to_it: 'Routed to IT',
  access_granted: 'Access updated',
  prep_ready: 'Copy ready → scans',
  security_passed: 'Security passed',
  security_failed: 'Security failed',
  security_waived: 'Security waived',
  code_review_passed: 'Code review passed → Development',
  code_review_failed: 'Code & architecture failed',
  code_review_waived: 'Code review waived → Development',
  development_complete: 'Development complete → Deployment',
  deployment_initiated: 'Initiate deployment → Deployment',
  deployment_succeeded: 'Deployment pipeline succeeded',
  advance_to_release: 'Deployment → Release',
  return_to_scans: 'Changes done — scan initiated again',
  return_to_development: 'Deployment → Development (fixes needed)',
  deployment_failed: 'Deployment failed',
  rerun_requested: 'Re-run requested',
  waiver_revoked: 'Waiver revoked',
  superseded: 'Superseded by a newer run',
};

function cycleEndLabel(cycle) {
  if (cycle?.isCurrent) return 'Current run';
  const reason = cycle?.endedReason;
  if (reason && REASON_LABELS[reason]) return REASON_LABELS[reason];
  if (cycle?.loopedBack) return 'Looped back';
  return 'Past run';
}

function statusLabel(status) {
  return STATUS_LABELS[status] || status || 'Not started';
}

function statusTone(status) {
  if (status === 'succeeded') return 'ok';
  if (status === 'failed') return 'bad';
  if (status === 'cancelled') return 'muted';
  if (ACTIVE.has(status)) return 'active';
  return 'muted';
}

function RemediationTodoList({ title, items, emptyHint, completedMap, onToggle, disabled }) {
  if (!items?.length) {
    return emptyHint ? (
      <div className="it-scan-todo-section">
        <h5>{title}</h5>
        <p className="it-scan-muted">{emptyHint}</p>
      </div>
    ) : null;
  }
  return (
    <div className="it-scan-todo-section">
      <h5>{title}</h5>
      <ul className="it-scan-todo-list">
        {items.map((t, idx) => {
          const key = t.key || `${title}-${idx}`;
          const checked = Boolean(
            completedMap && Object.prototype.hasOwnProperty.call(completedMap, key)
              ? completedMap[key]
              : t.completed
          );
          return (
            <li key={key} className={`it-scan-todo-item${checked ? ' is-done' : ''}`}>
              <label className="it-scan-todo-row">
                <input
                  type="checkbox"
                  className="it-scan-todo-check"
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) => onToggle?.(key, e.target.checked)}
                />
                <span className="it-scan-todo-body">
                  <span className="it-scan-todo-title">
                    {t.severity ? (
                      <span className={`it-scan-todo-sev sev-${String(t.severity).toLowerCase()}`}>
                        {String(t.severity).toUpperCase()}
                      </span>
                    ) : null}
                    {t.title}
                  </span>
                  {t.where ? <span className="it-scan-todo-where">{t.where}</span> : null}
                  {t.detail ? <span className="it-scan-todo-detail">{t.detail}</span> : null}
                  {Array.isArray(t.steps) && t.steps.length > 0 ? (
                    <ol className="it-scan-todo-steps">
                      {t.steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DevelopmentPlanBlock({
  plan,
  onDownloadPlan,
  completedMap,
  onToggleTodo,
  onSaveTodos,
  todoSaving,
  todoDirty,
  canEdit,
}) {
  if (!plan) return null;
  const deploymentTodos = plan.deploymentTodos || [];
  if (!deploymentTodos.length) {
    return (
      <p className="it-scan-muted">
        No deployment todos from the latest Code &amp; architecture review yet. You can still
        initiate deployment if the garage-apps copy is ready.
      </p>
    );
  }
  const done = Object.entries(completedMap || {}).filter(
    ([k, v]) => k.startsWith('deployment:') && v
  ).length;
  const total = plan.totalCount ?? deploymentTodos.length;
  return (
    <div className="it-scan-dev-plan">
      <div className="it-scan-dev-plan-head">
        <div>
          <h4>Deployment todos</h4>
          <p className="it-scan-todo-progress">
            {done}/{total} marked complete
            {todoSaving ? ' · saving…' : todoDirty ? ' · unsaved changes' : ''}
            {!canEdit ? ' · view only' : ''}
          </p>
        </div>
        <div className="it-scan-dev-plan-actions">
          {canEdit ? (
            <button
              type="button"
              className="garage-btn garage-btn-primary garage-btn-sm"
              disabled={todoSaving || !todoDirty}
              onClick={() => onSaveTodos?.()}
            >
              {todoSaving ? 'Saving…' : 'Save todos'}
            </button>
          ) : null}
          {plan.codeReviewJobId ? (
            <button
              type="button"
              className="garage-btn garage-btn-secondary garage-btn-sm"
              onClick={() => onDownloadPlan?.(plan.codeReviewJobId, 'plan_md', 'code-review-plan.md')}
            >
              <Download size={14} />
              Code review plan.md
            </button>
          ) : null}
        </div>
      </div>
      <RemediationTodoList
        title="Checklist"
        items={deploymentTodos}
        completedMap={completedMap}
        onToggle={onToggleTodo}
        disabled={!canEdit || todoSaving}
      />
    </div>
  );
}

function formatVerdict(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') {
    const key = raw.trim().toUpperCase().replace(/\s+/g, '_');
    return VERDICT_LABELS[key] || raw;
  }
  if (typeof raw === 'object') {
    const key = String(raw.verdict || raw.label || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');
    if (!key) return raw.summary ? String(raw.summary) : '';
    return VERDICT_LABELS[key] || key;
  }
  return String(raw);
}

function verdictKey(raw) {
  if (!raw) return '';
  if (typeof raw === 'string') return raw.trim().toUpperCase().replace(/\s+/g, '_');
  if (typeof raw === 'object') {
    return String(raw.verdict || raw.label || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_');
  }
  return '';
}

function shortSha(sha) {
  if (!sha) return '—';
  return String(sha).slice(0, 12);
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function timelineClass(metaState) {
  if (metaState === 'green' || metaState === 'approved') return 'completed';
  if (metaState === 'amber') return 'ready';
  if (metaState === 'blink') return 'active';
  if (metaState === 'red') return 'failed';
  return 'pending';
}

function SeverityChips({ counts }) {
  if (!counts || typeof counts !== 'object') return null;
  const order = ['critical', 'high', 'medium', 'low', 'info'];
  const items = order.filter((k) => Number(counts[k]) > 0);
  if (!items.length) return null;
  return (
    <div className="it-scan-sev-chips">
      {items.map((k) => (
        <span key={k} className={`it-scan-sev it-scan-sev-${k}`}>
          {k} {counts[k]}
        </span>
      ))}
    </div>
  );
}

function ScanSummaryBlock({ kind, summary }) {
  if (!summary) return null;
  if (kind === 'verdict') {
    const v = summary.verdict;
    const key = verdictKey(v);
    if (!key && !summary.grade) return null;
    const detail = typeof v === 'object' ? v : null;
    const blockers = detail?.blocker_checks || [];
    return (
      <div className="it-scan-summary-block">
        {key && (
          <div className={`it-scan-verdict-banner tone-${key.toLowerCase()}`}>
            <strong>{formatVerdict(v)}</strong>
            {detail?.summary && <span>{detail.summary}</span>}
          </div>
        )}
        <div className="it-scan-stats">
          {detail?.health_pct != null && (
            <span>
              Health <strong>{detail.health_pct}%</strong>
            </span>
          )}
          {detail?.blocker_count != null && (
            <span>
              Blockers <strong>{detail.blocker_count}</strong>
            </span>
          )}
          {summary.finding_count != null && (
            <span>
              Findings <strong>{summary.finding_count}</strong>
            </span>
          )}
        </div>
        {blockers.length > 0 && (
          <ul className="it-scan-blocker-list">
            {blockers.map((check) => (
              <li key={check}>{check}</li>
            ))}
          </ul>
        )}
        <SeverityChips counts={summary.severity_counts} />
      </div>
    );
  }
  if (!summary.grade) return null;
  return (
    <div className="it-scan-summary-block">
      <div className="it-scan-stats">
        <span>
          Grade{' '}
          <strong className={`it-scan-grade grade-${String(summary.grade).toLowerCase()}`}>
            {summary.grade}
          </strong>
        </span>
        <span>
          Findings <strong>{summary.finding_count ?? '—'}</strong>
        </span>
        <span>
          Health <strong>{summary.health_pct ?? '—'}%</strong>
        </span>
      </div>
      <SeverityChips counts={summary.severity_counts} />
    </div>
  );
}

function blockedSummary(waiver, kind) {
  const original = waiver?.originalResult || {};
  if (kind === 'verdict') {
    const v = formatVerdict(original.verdict);
    if (v) return v;
  }
  if (original.grade) return `Grade ${original.grade}`;
  if (original.status) return statusLabel(original.status);
  return 'Did not pass gate';
}

function WaiverNotice({ waiver, kind }) {
  if (!waiver) return null;
  const approver = (waiver.waivedByName || waiver.waivedBy || '').trim() || 'Unknown';
  return (
    <div className="it-scan-waiver-banner" role="status">
      <div className="it-scan-waiver-banner-title">Blocked — approved to proceed</div>
      <p className="it-scan-waiver-banner-lede">
        This scan was blocked ({blockedSummary(waiver, kind)}) and later approved.
      </p>
      <dl className="it-scan-waiver-meta">
        <div>
          <dt>Approved by</dt>
          <dd>{approver}</dd>
        </div>
        {waiver.waivedAt && (
          <div>
            <dt>Approved at</dt>
            <dd>{formatWhen(waiver.waivedAt)}</dd>
          </div>
        )}
        <div className="it-scan-waiver-comment">
          <dt>Comment</dt>
          <dd>{waiver.comment || '—'}</dd>
        </div>
      </dl>
    </div>
  );
}

function JobProgress({ job, progressEvents }) {
  if (!job || !ACTIVE.has(job.status)) return null;
  const pct = Math.max(0, Math.min(100, Number(job.progressPct) || 0));
  const trail = (progressEvents || []).slice(-6);
  return (
    <div className="it-scan-progress">
      <div className="it-scan-progress-head">
        <span className={`it-scan-status tone-${statusTone(job.status)}`}>
          {statusLabel(job.status)}
        </span>
        <span className="it-scan-progress-pct">{pct}%</span>
      </div>
      <div className="it-scan-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div style={{ width: `${pct}%` }} />
      </div>
      <p className="it-scan-muted">
        {job.progressMessage || job.progressStage || 'Waiting for worker…'}
      </p>
      {trail.length > 1 && (
        <ol className="it-scan-trail">
          {trail.map((event) => (
            <li key={event.id || `${event.stage}-${event.message}`}>
              {event.message || event.stage}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function ReportFrame({ jobId, status, theme }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const revoke = () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
    if (!jobId || status !== 'succeeded') {
      revoke();
      setUrl(null);
      setLoading(false);
      setMissing(false);
      return undefined;
    }
    setLoading(true);
    setMissing(false);
    (async () => {
      try {
        const blobUrl = await garageAPI.getSecScanDashboardObjectUrl(jobId);
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        revoke();
        urlRef.current = blobUrl;
        setUrl(blobUrl);
      } catch {
        if (!cancelled) {
          revoke();
          setUrl(null);
          setMissing(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      revoke();
    };
  }, [jobId, status]);

  if (!jobId || status !== 'succeeded') return null;

  return (
    <div className="it-scan-report">
      <div className="it-scan-report-head">
        <h5>HTML report</h5>
        {url && (
          <a className="garage-btn garage-btn-secondary garage-btn-sm" href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} />
            Open full report
          </a>
        )}
      </div>
      {loading && (
        <p className="it-scan-muted it-scan-report-loading">
          <Loader2 size={14} className="it-scan-spin" /> Loading report…
        </p>
      )}
      {!loading && url && (
        <iframe
          key={`${jobId}-${theme}`}
          title="Scan HTML report"
          src={`${url}#theme=${theme}`}
          sandbox="allow-scripts"
        />
      )}
      {!loading && missing && (
        <p className="it-scan-muted">No HTML dashboard artifact for this run yet.</p>
      )}
    </div>
  );
}

const ItScanFlowPanel = ({
  requestId,
  request,
  initialPayload = null,
  isAdmin,
  userEmail,
  userName,
  onRequestRefresh,
  onSendToOptimization,
  /** When true, land on Initiated and focus repo/token (requestor input path). */
  focusInitiated = false,
}) => {
  const [payload, setPayload] = useState(initialPayload);
  const [initialLoading, setInitialLoading] = useState(!initialPayload);
  const [selectedNode, setSelectedNode] = useState(() =>
    focusInitiated
      ? 'initiated'
      : nodeFromSubflow(initialPayload?.itReview?.subflowNode)
  );
  const [editRepoUrl, setEditRepoUrl] = useState('');
  const [editOriginRef, setEditOriginRef] = useState('master');
  const [editAccessToken, setEditAccessToken] = useState('');
  const initiatedFormRef = useRef(null);
  const [viewJobId, setViewJobId] = useState(null);
  /** null = follow current cycle; otherwise a scanCycles[].id */
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  /** Optional stage visit id within the selected run (previous version of a stage). */
  const [selectedStageVisitId, setSelectedStageVisitId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [waiveComment, setWaiveComment] = useState('');
  const [devNote, setDevNote] = useState('');
  const [deployedUrlModalOpen, setDeployedUrlModalOpen] = useState(false);
  const [releaseUrl, setReleaseUrl] = useState('');
  const [releaseUrlDirty, setReleaseUrlDirty] = useState(false);
  const [todoCompleted, setTodoCompleted] = useState({});
  const [todoSaving, setTodoSaving] = useState(false);
  const [todoDirty, setTodoDirty] = useState(false);
  const [showPat, setShowPat] = useState(false);
  const [gitServiceUser, setGitServiceUser] = useState('');
  const [runDetail, setRunDetail] = useState(null);
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-garage-theme') || 'light'
  );
  const selectedCycleIdRef = useRef(selectedCycleId);
  selectedCycleIdRef.current = selectedCycleId;
  /** When false, do not auto-jump selectedNode to the live subflow on poll/refresh. */
  const followActiveStageRef = useRef(true);
  const todoDirtyRef = useRef(false);
  todoDirtyRef.current = todoDirty;
  const todoCompletedRef = useRef(todoCompleted);
  todoCompletedRef.current = todoCompleted;

  const it = payload?.itReview;
  const accessDenied =
    (it?.accessStatus || '') === 'denied' ||
    (it?.onboardingPrepStatus || '') === 'denied';
  const prepFailed = (it?.onboardingPrepStatus || '') === 'failed';
  const nonRepo = Boolean(it?.itScansNotApplicable || request?.itScansNotApplicable);
  const inputRequired = Boolean(it?.itInputRequired || request?.itInputRequired);
  const visibleNodes = useMemo(() => {
    const order = Array.isArray(it?.nodeOrder) ? it.nodeOrder : null;
    if (!order || !order.length) {
      return nonRepo
        ? NODE_ORDER.filter((n) =>
            ['initiated', 'deployment', 'release'].includes(n.id)
          )
        : NODE_ORDER;
    }
    const idByKey = Object.fromEntries(NODE_ORDER.map((n) => [n.key, n]));
    return order
      .map((key) => idByKey[key] || NODE_ORDER.find((n) => n.id === key))
      .filter(Boolean);
  }, [it?.nodeOrder, nonRepo]);

  useEffect(() => {
    const url = it?.repoUrl || request?.repoUrl || '';
    const ref = it?.originRef || request?.originRef || 'master';
    setEditRepoUrl(url);
    setEditOriginRef(ref || 'master');
  }, [it?.repoUrl, it?.originRef, request?.repoUrl, request?.originRef, requestId]);

  useEffect(() => {
    let cancelled = false;
    garageAPI.getSecScanSettings()
      .then((s) => {
        if (!cancelled) setGitServiceUser(s?.gitServiceUser || '');
      })
      .catch(() => {
        if (!cancelled) setGitServiceUser('');
      });
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  useEffect(() => {
    if (releaseUrlDirty) return;
    const gem = (request?.appType || '').toLowerCase() === 'gem';
    const current = gem
      ? request?.gemUrl
      : request?.webUrl || request?.gemUrl;
    setReleaseUrl(current || '');
  }, [
    releaseUrlDirty,
    request?.appType,
    request?.gemUrl,
    request?.webUrl,
    requestId,
  ]);

  useEffect(() => {
    if (!focusInitiated) return;
    followActiveStageRef.current = false;
    setSelectedNode('initiated');
    setShowPat(false);
    const t = window.setTimeout(() => {
      initiatedFormRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 80);
    return () => window.clearTimeout(t);
  }, [focusInitiated, requestId]);

  useEffect(() => {
    if (!visibleNodes.some((n) => n.id === selectedNode)) {
      setSelectedNode('initiated');
    }
  }, [visibleNodes, selectedNode]);

  const load = useCallback(async () => {
    if (!requestId) return;
    try {
      const data = await garageAPI.getItReview(requestId);
      setPayload(data);
      setError(null);
      // Access-denied status/message render inline on the Initiated card; the
      // PAT modal only opens from an explicit click (never auto-popped by a poll).
      // Follow live stage only when the user has not pinned another stage / past cycle.
      if (
        followActiveStageRef.current &&
        !selectedCycleIdRef.current &&
        data?.itReview?.subflowNode
      ) {
        setSelectedNode(nodeFromSubflow(data.itReview.subflowNode));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load IT review');
    } finally {
      setInitialLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    if (initialPayload) {
      setPayload(initialPayload);
      setInitialLoading(false);
      if (
        followActiveStageRef.current &&
        !selectedCycleIdRef.current &&
        initialPayload?.itReview?.subflowNode
      ) {
        setSelectedNode(nodeFromSubflow(initialPayload.itReview.subflowNode));
      }
      return undefined;
    }
    // No hydrated payload from parent — fetch before rendering the flow.
    setInitialLoading(true);
    load();
    return undefined;
  }, [requestId, initialPayload, load]);

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setTheme(root.getAttribute('data-garage-theme') || 'light');
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-garage-theme'] });
    return () => observer.disconnect();
  }, []);

  const liveNodes = it?.nodes || {};
  const jobHistory = it?.jobHistory || { security: [], codeReview: [] };
  const scanCycles = it?.scanCycles || [];

  const selectedCycle = useMemo(() => {
    if (!scanCycles.length) return null;
    if (selectedCycleId) {
      return scanCycles.find((c) => c.id === selectedCycleId) || scanCycles.find((c) => c.isCurrent) || scanCycles[0];
    }
    return scanCycles.find((c) => c.isCurrent) || scanCycles[0];
  }, [scanCycles, selectedCycleId]);

  const viewingHistoricCycle = Boolean(selectedCycle && !selectedCycle.isCurrent);

  const selectedJobList = useMemo(() => {
    if (selectedNode === 'security') return jobHistory.security || [];
    if (selectedNode === 'codeReview') return jobHistory.codeReview || [];
    return [];
  }, [selectedNode, jobHistory]);

  const currentJob = useMemo(() => {
    if (selectedNode === 'security') return it?.security;
    if (selectedNode === 'codeReview') return it?.codeReview;
    return null;
  }, [it, selectedNode]);

  const cycleJobIdForNode = useMemo(() => {
    if (!selectedCycle) return null;
    if (selectedNode === 'security') return selectedCycle.securityJobId || null;
    if (selectedNode === 'codeReview') return selectedCycle.codeReviewJobId || null;
    return null;
  }, [selectedCycle, selectedNode]);

  const viewingJob = useMemo(() => {
    const findJob = (id) => {
      if (!id) return null;
      return selectedJobList.find((j) => j.id === id) || null;
    };
    const cycleSnap =
      selectedNode === 'security'
        ? selectedCycle?.security
        : selectedNode === 'codeReview'
          ? selectedCycle?.codeReview
          : null;

    // Prefer the job pinned by a history-card click.
    if (viewJobId) {
      const pinned = findJob(viewJobId);
      if (pinned) return pinned;
      if (cycleSnap?.id === viewJobId) {
        return { ...cycleSnap, summary: cycleSnap.summary || { grade: cycleSnap.grade, verdict: cycleSnap.verdict } };
      }
    }

    if (viewingHistoricCycle) {
      // Past run: only that cycle's job — never fall back to the live/current job.
      const fromCycle = findJob(cycleJobIdForNode);
      if (fromCycle) return fromCycle;
      if (cycleSnap?.id) {
        return {
          ...cycleSnap,
          summary: cycleSnap.summary || { grade: cycleSnap.grade, verdict: cycleSnap.verdict },
        };
      }
      return null;
    }

    if (cycleJobIdForNode) {
      const fromCycle = findJob(cycleJobIdForNode);
      if (fromCycle) return fromCycle;
    }
    return currentJob || selectedJobList[0] || null;
  }, [
    selectedCycle,
    selectedNode,
    selectedJobList,
    viewJobId,
    currentJob,
    viewingHistoricCycle,
    cycleJobIdForNode,
  ]);

  const paneWrapRef = useRef(null);

  const verdictKey = (raw) => {
    if (raw == null) return '';
    if (typeof raw === 'object') {
      return String(raw.verdict || raw.label || raw.status || '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '_');
    }
    return String(raw).trim().toUpperCase().replace(/\s+/g, '_');
  };

  /** Status of this block only (Passed / Failed / Grade / Verdict…) — not transition prose. */
  const blockStatusLabel = (stage) => {
    if (!stage) return '';
    if (stage.blockOutcome) return stage.blockOutcome;
    const reason = (stage.reason || '').trim();
    const node = (stage.node || '').trim();
    const vKey = verdictKey(stage.verdict);

    // Code & architecture: verdict / Failed / Running — never Security grades.
    if (node === 'code_review') {
      if (reason === 'code_review_failed' || stage.status === 'failed') return 'Failed';
      if (reason === 'code_review_waived') return 'Waived';
      if (stage.status === 'cancelled') return 'Cancelled';
      if (ACTIVE.has(stage.status) || reason === 'code_review_job') {
        return stage.status ? statusLabel(stage.status) : 'Queued';
      }
      if (vKey === 'NOT_READY') return 'Failed';
      if (vKey === 'READY_WITH_FIXES') return 'Ready with fixes';
      if (vKey === 'GO') return 'Passed';
      if (stage.verdict) return formatVerdict(stage.verdict);
      if (reason === 'code_review_passed') return 'Passed';
      if (stage.status === 'succeeded') return 'Passed';
      return '—';
    }

    // Security: grade / Passed / Failed — never code verdicts.
    if (node === 'security') {
      if (reason === 'security_failed' || stage.status === 'failed') return 'Failed';
      if (reason === 'security_waived') return 'Waived';
      if (stage.status === 'cancelled') return 'Cancelled';
      if (ACTIVE.has(stage.status) || reason === 'security_job') {
        return stage.status ? statusLabel(stage.status) : 'Queued';
      }
      if (stage.grade) return `Grade ${String(stage.grade).toUpperCase()}`;
      if (reason === 'security_passed' || stage.status === 'succeeded') return 'Passed';
      return '—';
    }

    if (stage.status === 'failed' || stage.status === 'cancelled') {
      return statusLabel(stage.status);
    }
    if (ACTIVE.has(stage.status)) return statusLabel(stage.status);

    switch (reason) {
      case 'deployment_failed':
        return node === 'deployment' ? 'Failed' : 'In progress';
      case 'prep_ready':
        return 'Passed';
      case 'development_complete':
        return node === 'deployment' ? 'Ready' : 'Complete';
      case 'return_to_development':
        return 'In progress';
      case 'return_to_scans':
      case 'rerun_requested':
        return node === 'initiated' ? 'Restarted' : 'Returned';
      case 'registered':
      case 'routed_to_it':
      case 'access_granted':
        return 'Started';
      case 'waiver_revoked':
        return 'Revoked';
      default:
        break;
    }

    if (node === 'development') return 'In progress';
    if (node === 'deployment') return 'Ready';
    if (node === 'release') return 'Pending';
    if (node === 'initiated') return 'Started';
    return '—';
  };

  const NODE_CARD_LABELS = {
    initiated: 'Initiated',
    security: 'Security scan',
    code_review: 'Code & architecture',
    development: 'Development',
    deployment: 'Deployment',
    release: 'Release',
  };

  const expandStagesForDisplay = (stages) => {
    // Backend already splits security/code pass-fail onto the completed block.
    // Only expand Deployment → Development loopbacks that still land as a single event.
    const out = [];
    const list = stages || [];
    for (const stage of list) {
      const reason = (stage.reason || '').trim();
      const fromNode = (stage.fromNode || '').trim();
      const alreadyHasFrom = list.some(
        (s) => s !== stage && s.node === fromNode && (s.reason || '').includes('fail')
      );
      const isDeployLoop =
        (reason === 'deployment_failed' || reason === 'return_to_development') &&
        fromNode &&
        fromNode !== stage.node &&
        !alreadyHasFrom;
      if (isDeployLoop) {
        out.push({
          ...stage,
          id: `${stage.id || 'stage'}-failed-block`,
          node: fromNode,
          label: stage.fromLabel || NODE_CARD_LABELS[fromNode] || fromNode,
          fromNode: null,
          fromLabel: null,
          blockOutcome: reason === 'deployment_failed' ? 'Failed' : 'Returned',
          jobId: null,
        });
        out.push({
          ...stage,
          blockOutcome: 'In progress',
        });
        continue;
      }
      out.push(stage);
    }
    return out;
  };

  const stagesForCycle = (cycle) => {
    if (cycle?.stages?.length) return expandStagesForDisplay(cycle.stages);
    const fallback = [];
    if (cycle?.security) {
      fallback.push({
        id: `fallback-sec-${cycle.securityJobId || cycle.id}`,
        node: 'security',
        label: 'Security scan',
        reason: 'security_job',
        jobId: cycle.securityJobId,
        status: cycle.security.status,
        grade: cycle.security.grade,
        at: cycle.startedAt,
      });
    }
    if (cycle?.codeReview) {
      fallback.push({
        id: `fallback-code-${cycle.codeReviewJobId || cycle.id}`,
        node: 'code_review',
        label: 'Code & architecture',
        reason: 'code_review_job',
        jobId: cycle.codeReviewJobId,
        status: cycle.codeReview.status,
        verdict: cycle.codeReview.verdict,
        at: cycle.codeReview.finishedAt || cycle.endedAt,
      });
    }
    return expandStagesForDisplay(fallback);
  };

  const blockStatusTone = (stage) => {
    const label = (blockStatusLabel(stage) || '').toLowerCase();
    const reason = (stage?.reason || '').trim();
    const vKey = verdictKey(stage?.verdict);
    const node = (stage?.node || '').trim();

    if (
      label === 'failed' ||
      label === 'cancelled' ||
      label === 'revoked' ||
      label === 'not ready' ||
      reason.endsWith('_failed') ||
      stage?.status === 'failed' ||
      (node === 'code_review' && vKey === 'NOT_READY')
    ) {
      return 'bad';
    }
    if (label === 'ready with fixes' || vKey === 'READY_WITH_FIXES') {
      return 'active';
    }
    if (
      label === 'passed' ||
      label === 'complete' ||
      label === 'ready' ||
      label === 'waived' ||
      label.startsWith('grade ') ||
      reason.endsWith('_passed') ||
      reason === 'development_complete'
    ) {
      return 'ok';
    }
    if (
      label === 'in progress' ||
      label === 'restarted' ||
      label === 'returned' ||
      label === 'scanning' ||
      label === 'queued' ||
      label === 'starting' ||
      label === 'finalizing' ||
      ACTIVE.has(stage?.status)
    ) {
      return 'active';
    }
    return 'muted';
  };

  const STAGE_NODE_TO_UI = {
    initiated: 'initiated',
    security: 'security',
    code_review: 'codeReview',
    development: 'development',
    deployment: 'deployment',
    release: 'release',
  };

  /** Rebuild left-rail circle states from a past run's stage path (not live IT state). */
  const nodesFromCycle = (cycle) => {
    const base = liveNodes || {};
    const order = visibleNodes.map((n) => n.id);
    const stages = stagesForCycle(cycle);
    const lastByUi = {};
    stages.forEach((stage) => {
      const ui = STAGE_NODE_TO_UI[stage.node];
      if (ui) lastByUi[ui] = stage;
    });
    const lastStage = stages[stages.length - 1];
    const furthest = lastStage ? STAGE_NODE_TO_UI[lastStage.node] : null;
    const furthestIdx = furthest ? order.indexOf(furthest) : -1;

    const toneToState = (stage, uiId) => {
      const tone = blockStatusTone(stage);
      const vKey = verdictKey(stage?.verdict);
      const grade = String(stage?.grade || '').toUpperCase();
      const reason = (stage?.reason || '').trim();
      if (tone === 'bad') return 'red';
      if (reason === 'security_waived' || reason === 'code_review_waived') return 'approved';
      if (tone === 'ok') {
        if (grade === 'C' || vKey === 'READY_WITH_FIXES') return 'amber';
        return 'green';
      }
      if (tone === 'active') {
        // Past runs are frozen — no blink. Furthest step = amber; earlier = progressed past.
        if (uiId === furthest) return 'amber';
        return 'green';
      }
      return uiId === furthest ? 'amber' : 'grey';
    };

    const out = {};
    order.forEach((id, idx) => {
      const live = base[id] || { id, label: NODE_ORDER.find((n) => n.id === id)?.label || id };
      const stage = lastByUi[id];
      if (stage) {
        out[id] = {
          ...live,
          state: toneToState(stage, id),
          grade:
            stage.grade ||
            (id === 'security' ? cycle.security?.grade : live.grade) ||
            null,
          verdict:
            stage.verdict ||
            (id === 'codeReview' ? cycle.codeReview?.verdict : live.verdict) ||
            null,
          hint: `Run ${cycle.attempt} history`,
          locked: false,
          retrying: false,
          needsUserToken: false,
        };
        return;
      }
      // Reached later steps imply earlier unmarked ones completed on that run.
      if (furthestIdx > idx) {
        out[id] = {
          ...live,
          state: 'green',
          grade: id === 'security' ? cycle.security?.grade || null : live.grade || null,
          verdict: id === 'codeReview' ? cycle.codeReview?.verdict || null : live.verdict || null,
          hint: `Completed in Run ${cycle.attempt}`,
          locked: false,
          retrying: false,
          needsUserToken: false,
        };
        return;
      }
      out[id] = {
        ...live,
        state: 'grey',
        grade: null,
        verdict: null,
        hint: `Not reached in Run ${cycle.attempt}`,
        locked: true,
        retrying: false,
        needsUserToken: false,
      };
    });
    return out;
  };

  const nodes = useMemo(() => {
    if (viewingHistoricCycle && selectedCycle) {
      return nodesFromCycle(selectedCycle);
    }
    return liveNodes;
  }, [viewingHistoricCycle, selectedCycle, liveNodes, visibleNodes]);

  const showCurrentRun = () => {
    followActiveStageRef.current = true;
    setSelectedCycleId(null);
    setSelectedStageVisitId(null);
    setViewJobId(null);
    if (it?.subflowNode) {
      setSelectedNode(nodeFromSubflow(it.subflowNode));
    }
  };

  const openStageVisit = (cycle, stage) => {
    if (!cycle || !stage) return;
    // Keep the run selected even when it is current so the transition card stays highlighted.
    setSelectedCycleId(cycle.id);
    setSelectedStageVisitId(stage.id);
    setHistoryOpen(true);
    const map = {
      initiated: 'initiated',
      security: 'security',
      code_review: 'codeReview',
      development: 'development',
      deployment: 'deployment',
      release: 'release',
    };
    const node = map[stage.node] || 'initiated';
    setSelectedNode(node);
    if (stage.jobId) {
      setViewJobId(stage.jobId);
    } else if (node === 'security' && (stage.securityJobId || cycle.securityJobId)) {
      setViewJobId(stage.securityJobId || cycle.securityJobId);
    } else if (node === 'codeReview' && (stage.codeReviewJobId || cycle.codeReviewJobId)) {
      setViewJobId(stage.codeReviewJobId || cycle.codeReviewJobId);
    } else {
      setViewJobId(null);
    }
    // Bring the step pane into view after state updates.
    requestAnimationFrame(() => {
      paneWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const openCycle = (cycle, preferNode) => {
    setHistoryOpen(true);
    const stages = stagesForCycle(cycle);
    const last = stages[stages.length - 1];
    if (last && !preferNode) {
      openStageVisit(cycle, last);
      return;
    }
    setSelectedCycleId(cycle.id);
    setSelectedStageVisitId(null);
    setViewJobId(null);
    if (preferNode) {
      setSelectedNode(preferNode);
    } else if (cycle.codeReviewJobId) {
      setSelectedNode('codeReview');
    } else if (cycle.securityJobId) {
      setSelectedNode('security');
    }
    requestAnimationFrame(() => {
      paneWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const scanActive = Boolean(viewingJob && ACTIVE.has(viewingJob.status));
  const anyScanActive = Boolean(
    (it?.security && ACTIVE.has(it.security.status)) ||
      (it?.codeReview && ACTIVE.has(it.codeReview.status)) ||
      ['pending', 'running', 'failed'].includes(it?.onboardingPrepStatus || '')
  );

  useEffect(() => {
    if (!requestId) return undefined;
    const status = request?.status || payload?.status;
    const activeStage = ['it_review', 'routed-to-it', 'deploying'].includes(status);
    if (!activeStage && !anyScanActive) return undefined;
    const ms = scanActive || anyScanActive ? ACTIVE_POLL_MS : POLL_MS;
    const t = setInterval(load, ms);
    return () => clearInterval(t);
  }, [requestId, request?.status, payload?.status, load, scanActive, anyScanActive]);

  useEffect(() => {
    // Don't yank the pane while browsing a past run or a manually selected stage.
    if (selectedCycleId || !followActiveStageRef.current) return;
    if (it?.subflowNode) {
      setSelectedNode(nodeFromSubflow(it.subflowNode));
    }
  }, [it?.subflowNode, selectedCycleId]);

  // When a new current cycle appears after re-run, stay on current unless user pinned a past cycle.
  useEffect(() => {
    if (!selectedCycleId) return;
    if (!scanCycles.some((c) => c.id === selectedCycleId)) {
      setSelectedCycleId(null);
    }
  }, [scanCycles, selectedCycleId]);

  useEffect(() => {
    let cancelled = false;
    const jobId = viewingJob?.id;
    if (!jobId) {
      setRunDetail(null);
      return undefined;
    }
    (async () => {
      try {
        const detail = await garageAPI.getSecScanRun(jobId);
        if (!cancelled) setRunDetail(detail);
      } catch {
        if (!cancelled) setRunDetail(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewingJob?.id, viewingJob?.status, viewingJob?.progressPct, viewingJob?.updatedAt]);

  const prepRetrying = Boolean(nodes?.initiated?.retrying) || prepFailed;
  const changesDetected = Boolean(it?.changesDetected ?? payload?.changesDetected);

  const handleRerun = async () => {
    setBusy(true);
    try {
      const data = await garageAPI.rerunItReview(requestId);
      setPayload(data);
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Re-run failed');
    } finally {
      setBusy(false);
    }
  };

  // Keep local checkbox state in sync with server plan (skip while unsaved edits exist).
  useEffect(() => {
    if (todoDirtyRef.current) return;
    const plan = it?.developmentPlan;
    if (!plan) {
      setTodoCompleted({});
      setTodoDirty(false);
      return;
    }
    const fromServer = { ...(plan.completed || {}) };
    for (const t of plan.deploymentTodos || []) {
      if (t?.key && fromServer[t.key] === undefined) {
        fromServer[t.key] = Boolean(t.completed);
      }
    }
    setTodoCompleted(fromServer);
    setTodoDirty(false);
  }, [it?.developmentPlan?.planKey, it?.developmentPlan?.completedCount, it?.developmentComplete]);

  const persistDevelopmentTodos = useCallback(async () => {
    if (!requestId) return;
    const nextMap = todoCompletedRef.current || {};
    setTodoSaving(true);
    try {
      const data = await garageAPI.updateItDevelopmentTodos(requestId, {
        completed: nextMap,
      });
      setTodoDirty(false);
      setPayload(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not save todo progress');
    } finally {
      setTodoSaving(false);
    }
  }, [requestId]);

  const handleToggleTodo = useCallback((key, done) => {
    if (!key) return;
    setTodoCompleted((prev) => {
      const next = { ...prev, [key]: Boolean(done) };
      todoCompletedRef.current = next;
      return next;
    });
    setTodoDirty(true);
  }, []);

  const handleInitiateDeployment = async () => {
    setBusy(true);
    try {
      const data = await garageAPI.initiateItDeployment(requestId, {
        note: devNote.trim() || undefined,
        completed: todoCompletedRef.current || todoCompleted,
      });
      setTodoDirty(false);
      setPayload(data);
      setDevNote('');
      followActiveStageRef.current = true;
      setSelectedNode('deployment');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not initiate deployment');
    } finally {
      setBusy(false);
    }
  };

  const handleReturnToScans = async (note) => {
    setBusy(true);
    try {
      const data = await garageAPI.returnItToScans(requestId, {
        note: (typeof note === 'string' ? note : devNote).trim() || undefined,
      });
      setPayload(data);
      setDevNote('');
      setSelectedCycleId(null);
      setSelectedStageVisitId(null);
      setViewJobId(null);
      followActiveStageRef.current = true;
      setSelectedNode('initiated');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not return to scans');
    } finally {
      setBusy(false);
    }
  };

  const handleReturnToDevelopment = async (reason = 'return_to_development') => {
    setBusy(true);
    try {
      const data = await garageAPI.returnItToDevelopment(requestId, {
        note: devNote.trim() || undefined,
        reason,
      });
      setPayload(data);
      setDevNote('');
      followActiveStageRef.current = true;
      setSelectedNode('development');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not return to Development');
    } finally {
      setBusy(false);
    }
  };

  const handleCompleteItReview = async () => {
    const url = releaseUrl.trim();
    if (url && !/^https?:\/\//i.test(url)) {
      setError('Deployed app URL must start with http:// or https://');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await garageAPI.completeItReview(requestId, {
        deployedUrl: url || null,
      });
      setPayload(data);
      setReleaseUrlDirty(false);
      followActiveStageRef.current = true;
      setSelectedNode('release');
      onRequestRefresh?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        (typeof detail === 'string' && detail)
          || err.message
          || 'Could not complete IT review'
      );
    } finally {
      setBusy(false);
    }
  };

  const handleNonRepoAdvance = async (stage) => {
    setBusy(true);
    try {
      const data = await garageAPI.advanceItNonRepo(requestId, { stage });
      setPayload(data);
      followActiveStageRef.current = true;
      if (stage === 'deployment') setSelectedNode('deployment');
      else setSelectedNode('release');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not change status');
    } finally {
      setBusy(false);
    }
  };

  const handleAdvanceToRelease = () => {
    setDeployedUrlModalOpen(true);
  };

  const confirmAdvanceToRelease = async ({ deployedUrl, note }) => {
    setBusy(true);
    try {
      const data = await garageAPI.advanceItToRelease(requestId, {
        deployedUrl,
        note: note || devNote.trim() || undefined,
      });
      setPayload(data);
      setDevNote('');
      followActiveStageRef.current = true;
      setSelectedNode('release');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not advance to Release');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const handleWaive = async (node) => {
    if (!waiveComment.trim()) return;
    setBusy(true);
    try {
      const data = await garageAPI.waiveItReview(requestId, {
        node,
        comment: waiveComment.trim(),
        adminEmail: userEmail,
        adminName: userName,
      });
      setPayload(data);
      setWaiveComment('');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Waive failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGrantAccess = async (accessToken, extras = {}) => {
    const body = { ...extras };
    if (accessToken) {
      body.accessToken = await encryptAccessTokenForTransport(accessToken);
    }
    const data = await garageAPI.grantItReviewAccess(requestId, body);
    setPayload(data);
    setEditAccessToken('');
    if ((data?.itReview?.accessStatus || '') !== 'denied') {
      setShowPat(false);
    }
    onRequestRefresh?.();
    return {
      accessStatus: data?.itReview?.accessStatus,
      accessMessage: data?.itReview?.accessMessage,
    };
  };

  const handleSaveRepoAccess = async () => {
    const url = (editRepoUrl || '').trim();
    if (!url) {
      setError('Repository URL is required.');
      return;
    }
    if (!/^https:\/\//i.test(url)) {
      setError('Repository URL must be an https:// git URL.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await handleGrantAccess(editAccessToken.trim() || undefined, {
        repoUrl: url || undefined,
        originRef: (editOriginRef || 'master').trim() || 'master',
      });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not save repo / access');
    } finally {
      setBusy(false);
    }
  };

  const handleInitiateScans = async () => {
    const url = (editRepoUrl || it?.repoUrl || request?.repoUrl || '').trim();
    if (!url) {
      setError('A repository URL is required before initiating scans.');
      return;
    }
    if (!/^https:\/\//i.test(url)) {
      setError('Repository URL must be an https:// git URL.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        adminEmail: userEmail,
        adminName: userName,
        repoUrl: url,
        originRef: (editOriginRef || it?.originRef || 'master').trim() || 'master',
      };
      const raw = (editAccessToken || '').trim();
      if (raw) {
        body.accessToken = await encryptAccessTokenForTransport(raw);
      }
      const data = await garageAPI.initiateItReviewScans(requestId, body);
      setPayload(data);
      setEditAccessToken('');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not initiate scans');
    } finally {
      setBusy(false);
    }
  };

  const handleAskRequestor = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await garageAPI.requestItReviewInput(requestId, {
        adminEmail: userEmail,
        adminName: userName,
      });
      setPayload(data);
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not request input');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleNonRepo = async (enabled) => {
    setBusy(true);
    setError(null);
    try {
      const data = await garageAPI.setItReviewNonRepo(requestId, {
        nonRepoRequest: Boolean(enabled),
        adminEmail: userEmail,
        adminName: userName,
      });
      setPayload(data);
      if (enabled) setSelectedNode('initiated');
      onRequestRefresh?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not update non-repo flag');
    } finally {
      setBusy(false);
    }
  };

  const downloadArtifact = async (kind, fileName) => {
    if (!viewingJob?.id) return;
    try {
      await garageAPI.downloadSecScanArtifact(viewingJob.id, kind, fileName);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Download failed');
    }
  };

  const downloadPlanForJob = async (jobId, kind, fileName) => {
    if (!jobId) return;
    try {
      await garageAPI.downloadSecScanArtifact(jobId, kind, fileName);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Download failed');
    }
  };

  const selectedStageVisit = useMemo(() => {
    if (!selectedCycle || !selectedStageVisitId) return null;
    return (selectedCycle.stages || []).find((s) => s.id === selectedStageVisitId) || null;
  }, [selectedCycle, selectedStageVisitId]);

  const viewingPastStageVersion = useMemo(() => {
    if (!selectedStageVisit || !selectedCycle) return false;
    if (!selectedCycle.isCurrent) return true;
    const stages = selectedCycle.stages || [];
    const lastOfNode = [...stages]
      .reverse()
      .find((s) => s.node === selectedStageVisit.node);
    return Boolean(lastOfNode && lastOfNode.id !== selectedStageVisit.id);
  }, [selectedStageVisit, selectedCycle]);

  const browsingPast = Boolean(viewingHistoricCycle || viewingPastStageVersion);

  const renderPastRunBanner = () => {
    if (!browsingPast || !selectedCycle) return null;
    const stageLabel = selectedStageVisit
      ? selectedStageVisit.label || selectedStageVisit.node
      : null;
    return (
      <div className="it-scan-past-run-banner" role="status">
        <div className="it-scan-past-run-banner-text">
          <strong>Past run — not the active run</strong>
          <span>
            Viewing{' '}
            <strong>
              Run {selectedCycle.attempt}
              {!selectedCycle.isCurrent ? '' : ' (earlier step)'}
            </strong>
            {stageLabel ? ` · ${stageLabel}` : ''}
            {selectedStageVisit?.at ? ` · ${formatWhen(selectedStageVisit.at)}` : ''}
            {selectedStageVisit?.note ? ` — ${selectedStageVisit.note}` : ''}.
            {' '}Read-only snapshot of this history entry.
          </span>
        </div>
        <button
          type="button"
          className="garage-btn garage-btn-secondary garage-btn-sm"
          onClick={showCurrentRun}
        >
          Show current run
        </button>
      </div>
    );
  };

  const renderScanPane = ({ title, kind, nodeMeta, waiver, waiveNode }) => {
    const failed = nodeMeta?.state === 'red';
    const merged =
      runDetail?.id === viewingJob?.id ? { ...viewingJob, ...runDetail } : viewingJob;
    const summary = merged?.summary || {};
    const artifacts = merged?.artifacts || [];
    const isHistoric = Boolean(
      browsingPast ||
        (viewingJob?.id && currentJob?.id && viewingJob.id !== currentJob.id)
    );
    const isWaived = Boolean(waiver);
    const grade = String(summary?.grade || '').toUpperCase();
    const verdict = String(
      typeof summary?.verdict === 'object'
        ? summary?.verdict?.verdict || summary?.verdict?.label || ''
        : summary?.verdict || ''
    )
      .toUpperCase()
      .replace(/\s+/g, '_');
    const outcomeFailed =
      !isWaived &&
      (failed ||
        merged?.status === 'failed' ||
        (kind === 'grade' &&
          merged?.status === 'succeeded' &&
          grade &&
          !['A', 'B', 'C'].includes(grade)) ||
        (kind === 'verdict' &&
          merged?.status === 'succeeded' &&
          verdict &&
          !['GO', 'READY_WITH_FIXES'].includes(verdict)));
    const canReinitiateScan =
      Boolean(isAdmin) &&
      outcomeFailed &&
      !isHistoric &&
      !viewingHistoricCycle &&
      !viewingPastStageVersion;

    return (
      <div className={`it-scan-pane it-scan-pane-scan${isHistoric ? ' is-past-run' : ''}`}>
        {renderPastRunBanner()}
        <div className="it-scan-pane-head">
          <div>
            <h4>
              {title}
              {isHistoric ? (
                <span className="it-scan-past-run-tag">Past run</span>
              ) : null}
            </h4>
            <p className="it-scan-muted">
              {merged?.llmProvider || '—'}
              {merged?.ref ? ` · ${merged.ref}` : ''}
              {isHistoric && selectedCycle ? ` · Run ${selectedCycle.attempt}` : ''}
            </p>
          </div>
          <div className="it-scan-pane-head-badges">
            {isWaived && !isHistoric ? (
              <>
                <span className="it-scan-status tone-bad">Blocked</span>
                <span className="it-scan-status tone-ok">Approved</span>
              </>
            ) : (
              <span className={`it-scan-status tone-${statusTone(merged?.status)}`}>
                {statusLabel(merged?.status)}
              </span>
            )}
          </div>
        </div>

        {!merged && isHistoric ? (
          <p className="it-scan-muted" role="status">
            No scan job was recorded for this step in Run {selectedCycle?.attempt}.
          </p>
        ) : null}

        {!isHistoric && <WaiverNotice waiver={waiver} kind={kind} />}

        <JobProgress job={merged} progressEvents={merged?.progress} />

        {merged?.errorMessage && (
          <div className="it-scan-error-box">{merged.errorMessage}</div>
        )}

        {merged && !ACTIVE.has(merged?.status) && (
          <ScanSummaryBlock kind={kind} summary={summary} />
        )}

        {artifacts.length > 0 && (
          <div className="it-scan-downloads">
            {[...artifacts]
              .sort((a, b) => {
                const order = {
                  plan_md: 0,
                  deployment_todos_json: 1,
                  dashboard_html: 2,
                  report_md: 3,
                  report_xlsx: 4,
                  findings_json: 5,
                };
                return (order[a.kind] ?? 50) - (order[b.kind] ?? 50);
              })
              .map((a) => (
              <button
                key={a.kind}
                type="button"
                className="garage-btn garage-btn-secondary garage-btn-sm"
                onClick={() => downloadArtifact(a.kind, a.fileName)}
              >
                <Download size={14} />
                {a.fileName || a.kind}
              </button>
            ))}
          </div>
        )}

        {canReinitiateScan && (
          <div className="it-scan-actions it-scan-rescan-actions">
            <p className="it-scan-lede">
              This scan did not pass. Fix issues on the garage-apps copy, then start a new
              scan run — or send the request back to Optimization with notes/files for the
              requestor. The flow will not move ahead until scans pass (or are waived).
            </p>
            <button
              type="button"
              className="garage-btn garage-btn-primary"
              disabled={busy}
              onClick={() =>
                handleReturnToScans(
                  `${title} failed — changes done, initiate scan again`
                )
              }
            >
              Changes done, initiate scan again
            </button>
            {onSendToOptimization ? (
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                disabled={busy}
                onClick={() => onSendToOptimization(`${title} failed`)}
              >
                Send to Optimization
              </button>
            ) : null}
          </div>
        )}

        {isAdmin && outcomeFailed && !waiver && !isHistoric && (
          <div className="it-scan-waive-box">
            <textarea
              className="garage-textarea"
              rows={3}
              placeholder="Admin waiver comment (required)"
              value={waiveComment}
              onChange={(e) => setWaiveComment(e.target.value)}
            />
            <button
              type="button"
              className="garage-btn garage-btn-primary"
              disabled={!waiveComment.trim() || busy}
              onClick={() => handleWaive(waiveNode)}
            >
              Approve / Waive
            </button>
          </div>
        )}

        {merged?.id ? (
          <ReportFrame jobId={merged.id} status={merged?.status} theme={theme} />
        ) : null}
      </div>
    );
  };

  const renderPane = () => {
    if (!it) {
      return <p className="it-scan-muted">Waiting for IT scan registration…</p>;
    }

    if (selectedNode === 'initiated') {
      const prepHeld = (it.onboardingPrepStatus || '') === 'held' || !it.onboardingPrepStatus;
      const initiatedTone = nonRepo
        ? 'ok'
        : nodes.initiated?.state === 'green'
          ? 'ok'
          : nodes.initiated?.state === 'red' || prepFailed
            ? 'bad'
            : 'active';
      const initiatedLabel = nonRepo
        ? 'scans N/A'
        : prepFailed
          ? 'failed'
          : nodes.initiated?.retrying
            ? 'failed'
            : prepHeld && !it.garageCopyUrl
              ? 'awaiting initiate'
              : it.onboardingPrepStatus || it.accessStatus || '—';
      const mondayUrl = it.mondayItemUrl || request?.mondayItemUrl;
      const repoMissing = !(it.repoUrl || request?.repoUrl || '').trim();
      const isRequestor = (request?.requesterEmail || '').toLowerCase()
        === (userEmail || '').toLowerCase();
      const requestorSuppliesRepo = Boolean(
        isRequestor && (inputRequired || repoMissing || accessDenied)
      );
      return (
        <div className={`it-scan-pane${browsingPast ? ' is-past-run' : ''}`}>
          {renderPastRunBanner()}
          <div className="it-scan-pane-head">
            <h4>
              Initiated
              {browsingPast ? <span className="it-scan-past-run-tag">Past run</span> : null}
            </h4>
            <span className={`it-scan-status tone-${initiatedTone}`}>
              {browsingPast
                ? (blockStatusLabel(selectedStageVisit) || 'Past')
                : initiatedLabel}
            </span>
          </div>
          <p className="it-scan-lede">
            {browsingPast
              ? 'Snapshot from run history. Switch back to the current run to make changes.'
              : nonRepo
                ? 'Non-repo request — Security / Code Review / Deployment are skipped.'
                : 'Monday ticket and access setup. Scans start only after Initiate scans.'}
          </p>
          {browsingPast && selectedStageVisit && (
            <p className="it-scan-changes">
              {REASON_LABELS[selectedStageVisit.reason] || selectedStageVisit.reason}
              {selectedStageVisit.note ? ` — ${selectedStageVisit.note}` : ''}
              {selectedStageVisit.at ? ` · ${formatWhen(selectedStageVisit.at)}` : ''}
            </p>
          )}
          {inputRequired && (
            <p className="it-scan-changes" role="status">
              {requestorSuppliesRepo
                ? 'Input required — provide the repository URL and access token below.'
                : 'Input required — the requestor needs to provide the repository URL and access token.'}
            </p>
          )}
          <dl className="it-scan-meta">
            <div>
              <dt>Monday ticket</dt>
              <dd>
                {mondayUrl ? (
                  <a href={mondayUrl} target="_blank" rel="noreferrer">
                    Open in Monday <ExternalLink size={12} />
                  </a>
                ) : (
                  'Not linked yet'
                )}
              </dd>
            </div>
            <div>
              <dt>Access</dt>
              <dd>
                {nonRepo
                  ? 'N/A'
                  : `${it.accessStatus || '—'}${it.accessMessage ? ` — ${it.accessMessage}` : ''}`}
              </dd>
            </div>
            {!nonRepo && (
              <>
                <div>
                  <dt>Repository</dt>
                  <dd className="it-scan-mono">
                    {(it.repoUrl || request?.repoUrl || '').trim() || 'Not provided yet'}
                  </dd>
                </div>
                <div>
                  <dt>Branch</dt>
                  <dd>{it.originRef || request?.originRef || 'master'}</dd>
                </div>
                <div>
                  <dt>Garage copy</dt>
                  <dd>
                    {it.garageCopyUrl ? (
                      <a href={it.garageCopyUrl} target="_blank" rel="noreferrer">
                        {it.garageCopyUrl}
                      </a>
                    ) : (
                      'Not created yet'
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Copy HEAD</dt>
                  <dd className="it-scan-mono" title={it.copyHeadSha || ''}>
                    {shortSha(it.copyHeadSha)}
                  </dd>
                </div>
                <div>
                  <dt>Last scanned</dt>
                  <dd className="it-scan-mono" title={it.lastScannedSha || ''}>
                    {shortSha(it.lastScannedSha)}
                  </dd>
                </div>
              </>
            )}
          </dl>

          {!browsingPast && (
          <div className="it-scan-initiated-form" ref={initiatedFormRef}>
            {!nonRepo && requestorSuppliesRepo && (
              <>
                <ServiceAccountAccessSteps serviceUser={gitServiceUser} />
                <div className="garage-form-group">
                  <label htmlFor="it-initiated-repo-url">Repository URL</label>
                  <input
                    id="it-initiated-repo-url"
                    type="url"
                    className="garage-input"
                    value={editRepoUrl}
                    onChange={(e) => setEditRepoUrl(e.target.value)}
                    placeholder="https://git.ringcentral.com/org/app.git"
                    disabled={busy}
                  />
                </div>
                <div className="garage-form-group">
                  <label htmlFor="it-initiated-origin-ref">Branch</label>
                  <input
                    id="it-initiated-origin-ref"
                    type="text"
                    className="garage-input"
                    value={editOriginRef}
                    onChange={(e) => setEditOriginRef(e.target.value)}
                    placeholder="master"
                    disabled={busy}
                  />
                </div>
                <div className="garage-form-group">
                  <label htmlFor="it-initiated-token">
                    Access token <span className="garage-optional">(Optional)</span>
                  </label>
                  <input
                    id="it-initiated-token"
                    type="password"
                    autoComplete="off"
                    className="garage-input"
                    value={editAccessToken}
                    onChange={(e) => setEditAccessToken(e.target.value)}
                    placeholder="Optional if the service account is already a Reporter"
                    disabled={busy}
                  />
                </div>
              </>
            )}
            {!nonRepo && isAdmin && !isRequestor && repoMissing && !inputRequired && (
              <p className="it-scan-changes" role="status">
                The requestor has been asked to provide the repository URL and access token.
              </p>
            )}

            {isAdmin && (
              <label className="garage-checkbox-label" style={{ display: 'block', marginBottom: '0.75rem' }}>
                <input
                  type="checkbox"
                  checked={nonRepo}
                  onChange={(e) => handleToggleNonRepo(e.target.checked)}
                  disabled={busy}
                />
                {' '}
                Non-repo / packaged (scans not applicable — Deploy and Release stay available)
              </label>
            )}
          </div>
          )}

          {prepFailed && !accessDenied && !nonRepo && (
            <p className="it-scan-error" style={{ marginTop: '0.75rem' }}>
              Copy/admin step failed
              {prepRetrying ? ' — worker may retry, or use Retry prep now.' : '.'}
            </p>
          )}
          {changesDetected && !nonRepo && (
            <p className="it-scan-changes">Changes detected on the garage-apps copy.</p>
          )}
          {!browsingPast && (
          <div className="it-scan-actions">
            {!nonRepo && requestorSuppliesRepo && (
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                disabled={busy}
                onClick={handleSaveRepoAccess}
              >
                Save repo / token
              </button>
            )}
            {nonRepo && isAdmin && (request?.status || payload?.status) !== 'published' && (
              <button
                type="button"
                className="garage-btn garage-btn-primary"
                disabled={busy || (it.subflowNode || 'initiated') !== 'initiated'}
                onClick={() => handleNonRepoAdvance('deployment')}
              >
                Move to Deploy
              </button>
            )}
            {!nonRepo && isAdmin && (
              <>
                <button
                  type="button"
                  className="garage-btn garage-btn-primary"
                  disabled={
                    busy
                    || (!(editRepoUrl || it.repoUrl || '').trim())
                    || (it.canInitiateScans === false
                      && Boolean((it.repoUrl || '').trim())
                      && !['', 'held', 'failed', 'denied'].includes(
                        it.onboardingPrepStatus || ''
                      ))
                  }
                  onClick={handleInitiateScans}
                  title="Start access check, copy, and scans"
                >
                  Initiate scans
                </button>
                <button
                  type="button"
                  className="garage-btn garage-btn-secondary"
                  disabled={busy}
                  onClick={() => setShowPat(true)}
                >
                  Update token
                </button>
                <button
                  type="button"
                  className="garage-btn garage-btn-secondary"
                  disabled={busy}
                  onClick={handleAskRequestor}
                >
                  Ask requestor for input
                </button>
              </>
            )}
            {!nonRepo && accessDenied && !isAdmin && !requestorSuppliesRepo && (
              <p className="it-scan-error" role="status">
                Access denied{it.accessMessage ? ` — ${it.accessMessage}` : '.'}
              </p>
            )}
            {!nonRepo && (prepFailed || changesDetected) && isAdmin && (
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                disabled={busy}
                onClick={handleRerun}
              >
                {changesDetected ? 'Re-sync & re-scan' : 'Retry prep now'}
              </button>
            )}
          </div>
          )}
        </div>
      );
    }

    if (selectedNode === 'security') {
      return renderScanPane({
        title: 'Security scan',
        kind: 'grade',
        nodeMeta: nodes.security,
        waiver: it.securityWaiver,
        waiveNode: 'security',
      });
    }

    if (selectedNode === 'codeReview') {
      return renderScanPane({
        title: 'Code & architecture',
        kind: 'verdict',
        nodeMeta: nodes.codeReview,
        waiver: it.codeReviewWaiver,
        waiveNode: 'code_review',
      });
    }

    if (selectedNode === 'development') {
      const unlocked = Boolean(it.scansPassed);
      const done = Boolean(it.developmentComplete);
      return (
        <div className={`it-scan-pane${browsingPast ? ' is-past-run' : ''}`}>
          {renderPastRunBanner()}
          <div className="it-scan-pane-head">
            <h4>
              Development
              {browsingPast ? <span className="it-scan-past-run-tag">Past run</span> : null}
            </h4>
            <span className={`it-scan-status tone-${done ? 'ok' : unlocked ? 'active' : 'muted'}`}>
              {browsingPast
                ? (blockStatusLabel(selectedStageVisit) || 'Past')
                : done
                  ? 'Complete'
                  : unlocked
                    ? 'In progress'
                    : 'Waiting on scans'}
            </span>
          </div>
          <p className="it-scan-lede">
            {browsingPast
              ? 'Snapshot from run history. Switch back to the current run to edit todos or initiate deployment.'
              : 'Complete the deployment todos on the garage-apps copy (CI/CD, Dockerfile, envs, merge to main). Then initiate deployment to trigger the GitLab pipeline on main — or re-initiate scans after code changes.'}
          </p>
          {it.garageCopyUrl && (
            <p className="it-scan-copy-link">
              <a href={it.garageCopyUrl} target="_blank" rel="noreferrer">
                Open garage-apps copy <ExternalLink size={12} />
              </a>
            </p>
          )}
          {(it.loopbackCount || 0) > 0 && (
            <p className="it-scan-changes">Loopbacks so far: {it.loopbackCount}</p>
          )}
          {selectedStageVisit && selectedStageVisit.node === 'development' && (
            <p className="it-scan-changes">
              {REASON_LABELS[selectedStageVisit.reason] || selectedStageVisit.reason}
              {selectedStageVisit.note ? ` — ${selectedStageVisit.note}` : ''}
              {selectedStageVisit.at ? ` · ${formatWhen(selectedStageVisit.at)}` : ''}
            </p>
          )}

          <DevelopmentPlanBlock
            plan={it.developmentPlan}
            onDownloadPlan={downloadPlanForJob}
            completedMap={todoCompleted}
            onToggleTodo={handleToggleTodo}
            onSaveTodos={persistDevelopmentTodos}
            todoSaving={todoSaving}
            todoDirty={todoDirty}
            canEdit={Boolean(
              isAdmin && unlocked && !done && !viewingHistoricCycle && !viewingPastStageVersion
            )}
          />

          {isAdmin && unlocked && !done && !viewingHistoricCycle && !viewingPastStageVersion && (
            <>
              <textarea
                className="garage-textarea"
                rows={2}
                placeholder="Optional note before initiating deployment"
                value={devNote}
                onChange={(e) => setDevNote(e.target.value)}
              />
              <div className="it-scan-actions">
                <button
                  type="button"
                  className="garage-btn garage-btn-primary"
                  disabled={busy}
                  onClick={handleInitiateDeployment}
                >
                  Initiate deployment
                </button>
                <button
                  type="button"
                  className="garage-btn garage-btn-secondary"
                  disabled={busy}
                  onClick={() => handleReturnToScans()}
                  title="Re-sync copy and re-run Security → Code review"
                >
                  Changes done, initiate scan again
                </button>
              </div>
            </>
          )}
          {done && !viewingPastStageVersion && (
            <p className="it-scan-waiver">
              Deployment initiated — the worker will trigger the GitLab pipeline on main.
            </p>
          )}
          {!unlocked && (
            <p className="it-scan-muted">
              Complete Security and Code & architecture (or waive) before deployment.
            </p>
          )}
        </div>
      );
    }

    if (selectedNode === 'deployment') {
      const deployUnlocked = !nodes.deployment?.locked;
      const deploy = it.deployment || {};
      const uiStatus = deploy.uiStatus;
      const statusTone =
        uiStatus === 'done' ? 'ok' : uiStatus === 'error' ? 'bad' : uiStatus === 'in_progress' ? 'active' : deployUnlocked ? 'ok' : 'muted';
      const statusLabel =
        uiStatus === 'done'
          ? 'Done'
          : uiStatus === 'error'
            ? 'Error'
            : uiStatus === 'in_progress'
              ? 'In progress'
              : deployUnlocked
                ? 'Queued'
                : 'Locked';
      return (
        <div className={`it-scan-pane${browsingPast ? ' is-past-run' : ''}`}>
          {renderPastRunBanner()}
          <div className="it-scan-pane-head">
            <h4>
              Deployment
              {browsingPast ? <span className="it-scan-past-run-tag">Past run</span> : null}
            </h4>
            <span className={`it-scan-status tone-${statusTone}`}>
              {browsingPast
                ? (blockStatusLabel(selectedStageVisit) || statusLabel)
                : statusLabel}
            </span>
          </div>
          <p className="it-scan-lede">
            {browsingPast
              ? 'Snapshot from run history. Live pipeline status below is only shown for the current run.'
              : nonRepo
                ? 'Packaged request — move this step to Release when deploy is done. No GitLab pipeline is required.'
                : deployUnlocked
                  ? 'GitLab pipeline on main — status updates as the worker polls. On failure, return to Development to fix and re-initiate.'
                  : 'Locked until you Initiate deployment from the Development step.'}
          </p>
          {!browsingPast && (deploy.status || deploy.pipelineId) && (
            <div className="it-scan-dev-plan" style={{ marginBottom: '0.85rem' }}>
              <p className="it-scan-todo-progress" style={{ margin: 0 }}>
                Queue: <strong>{deploy.status || '—'}</strong>
                {deploy.pipelineStatus ? ` · GitLab: ${deploy.pipelineStatus}` : ''}
                {deploy.ref ? ` · ref: ${deploy.ref}` : ''}
              </p>
              {deploy.pipelineUrl ? (
                <p className="it-scan-copy-link" style={{ marginTop: '0.45rem', marginBottom: 0 }}>
                  <a href={deploy.pipelineUrl} target="_blank" rel="noreferrer">
                    Open GitLab pipeline <ExternalLink size={12} />
                  </a>
                </p>
              ) : null}
              {deploy.error ? (
                <div className="it-scan-error-box" style={{ marginTop: '0.55rem' }}>
                  {deploy.error}
                </div>
              ) : null}
            </div>
          )}
          {selectedStageVisit && selectedStageVisit.node === 'deployment' && (
            <p className="it-scan-changes">
              {REASON_LABELS[selectedStageVisit.reason] || selectedStageVisit.reason}
              {selectedStageVisit.note ? ` — ${selectedStageVisit.note}` : ''}
              {selectedStageVisit.at ? ` · ${formatWhen(selectedStageVisit.at)}` : ''}
            </p>
          )}
          {nonRepo && isAdmin && !viewingHistoricCycle && !viewingPastStageVersion && (
            <div className="it-scan-actions">
              <button
                type="button"
                className="garage-btn garage-btn-primary"
                disabled={busy || (request?.status || payload?.status) === 'published'}
                onClick={() => handleNonRepoAdvance('release')}
              >
                Move to Release
              </button>
            </div>
          )}
          {isAdmin && !nonRepo && deployUnlocked && !viewingHistoricCycle && !viewingPastStageVersion && (
            <>
              <textarea
                className="garage-textarea"
                rows={2}
                placeholder={
                  uiStatus === 'done'
                    ? 'Optional note before Release'
                    : 'Optional note (what failed / what to fix)'
                }
                value={devNote}
                onChange={(e) => setDevNote(e.target.value)}
              />
              <div className="it-scan-actions">
                {uiStatus === 'done' ? (
                  <button
                    type="button"
                    className="garage-btn garage-btn-primary"
                    disabled={busy}
                    onClick={handleAdvanceToRelease}
                  >
                    Go to Release
                  </button>
                ) : null}
                {uiStatus !== 'done' ? (
                  <button
                    type="button"
                    className="garage-btn garage-btn-secondary"
                    disabled={busy}
                    onClick={() => handleReturnToDevelopment('deployment_failed')}
                    title="Record Deployment → Development in run history"
                  >
                    Deployment failed — back to Development
                  </button>
                ) : null}
                {uiStatus !== 'done' && onSendToOptimization ? (
                  <button
                    type="button"
                    className="garage-btn garage-btn-secondary"
                    disabled={busy}
                    onClick={() => onSendToOptimization('Deployment failed')}
                  >
                    Send to Optimization
                  </button>
                ) : null}
                <button
                  type="button"
                  className="garage-btn garage-btn-secondary"
                  disabled={busy}
                  onClick={() => handleReturnToDevelopment('return_to_development')}
                >
                  Return to Development
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    const deployedUrl =
      (request?.appType || '').toLowerCase() === 'gem'
        ? request?.gemUrl
        : request?.webUrl || request?.gemUrl;
    const mainStatus = String(request?.status || payload?.status || '')
      .trim()
      .toLowerCase()
      .replace(/-/g, '_');
    const itReviewOpen = mainStatus === 'it_review' || mainStatus === 'routed_to_it';
    const reviewComplete = mainStatus === 'deploying' || mainStatus === 'published';
    const onReleaseStep = (it?.subflowNode || 'initiated') === 'release';
    return (
      <div className={`it-scan-pane${browsingPast ? ' is-past-run' : ''}`}>
        {renderPastRunBanner()}
        <div className="it-scan-pane-head">
          <h4>
            Release
            {browsingPast ? <span className="it-scan-past-run-tag">Past run</span> : null}
          </h4>
          <span
            className={`it-scan-status tone-${
              reviewComplete ? 'ok' : 'muted'
            }`}
          >
            {browsingPast
              ? (blockStatusLabel(selectedStageVisit) || 'Past')
              : reviewComplete
                ? 'Complete'
                : 'Pending'}
          </span>
        </div>
        <p>
          {browsingPast
            ? 'Snapshot from run history. Switch back to the current run to complete release.'
            : mainStatus === 'published'
              ? 'Published.'
              : reviewComplete
                ? 'IT review is complete. This request is in Deploying.'
                : itReviewOpen && onReleaseStep
                  ? 'Add the deployed app URL if you have one, then mark IT review complete. That moves the request to Deploying and marks IT review complete.'
                  : itReviewOpen
                    ? 'Release is the last IT step. Move here from Deploy, then mark IT review complete.'
                    : 'IT review is complete. The request has moved to the next stage.'}
        </p>
        {isAdmin && itReviewOpen && onReleaseStep && !browsingPast && (
          <div className="garage-form-group">
            <label htmlFor="it-release-deployed-url">
              Deployed app URL <span className="garage-optional">(optional)</span>
            </label>
            <input
              id="it-release-deployed-url"
              type="url"
              className="garage-input"
              value={releaseUrl}
              placeholder="https://"
              disabled={busy}
              onChange={(e) => {
                setReleaseUrlDirty(true);
                setReleaseUrl(e.target.value);
              }}
            />
            <div className="it-scan-actions">
              <button
                type="button"
                className="garage-btn garage-btn-primary"
                disabled={busy}
                onClick={handleCompleteItReview}
              >
                Mark IT review complete
              </button>
            </div>
          </div>
        )}
        {(!itReviewOpen || !isAdmin || !onReleaseStep) && (
          <dl className="it-scan-dl" style={{ marginTop: '0.75rem' }}>
            <dt>Deployed app URL</dt>
            <dd>
              {deployedUrl ? (
                <a href={deployedUrl} target="_blank" rel="noreferrer">
                  {deployedUrl} <ExternalLink size={12} />
                </a>
              ) : (
                'Not set yet'
              )}
            </dd>
          </dl>
        )}
      </div>
    );
  };

  if (initialLoading) {
    return (
      <section className="it-scan-flow-panel it-scan-flow-panel-loading" aria-label="IT review flow">
        <GarageLoadingScreen compact message="Loading IT review…" />
      </section>
    );
  }

  return (
    <section className="it-scan-flow-panel" aria-label="IT review flow">
      <div className="it-scan-subtitle-row">
        <div>
          <h3 className="it-scan-subtitle">IT scan flow</h3>
          <p className="it-scan-muted">
            {nonRepo
              ? 'Initiated → Deploy → Release (scans not applicable)'
              : 'Initiated → Scans → Development (deploy prep / fixes) → Deployment → Release'}
          </p>
        </div>
        <div className="it-scan-subtitle-badges">
          {nonRepo && (
            <span className="it-scan-status tone-ok">Non-repo</span>
          )}
          {(it?.loopbackCount || 0) > 0 && (
            <span className="it-scan-status tone-active">{it.loopbackCount} loopback(s)</span>
          )}
          {it?.gatesSatisfied && (
            <span className="it-scan-status tone-ok">Gates satisfied</span>
          )}
        </div>
      </div>

      {scanCycles.length > 0 && (
        <div className={`it-scan-cycle-bar${historyOpen ? ' is-open' : ''}`} aria-label="Run history">
          <button
            type="button"
            className="it-scan-cycle-toggle"
            onClick={() => setHistoryOpen((v) => !v)}
            aria-expanded={historyOpen}
          >
            <span className="it-scan-cycle-toggle-left">
              {historyOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span className="it-scan-cycle-toggle-title">Run history</span>
              <span className="it-scan-cycle-toggle-count">
                {scanCycles.length} run{scanCycles.length === 1 ? '' : 's'}
                {scanCycles.some((c) => c.loopedBack) ? ' · includes loopbacks' : ''}
              </span>
            </span>
            <span className="it-scan-muted">{historyOpen ? 'Collapse' : 'Expand'}</span>
          </button>
          {historyOpen && (
            <>
              <p className="it-scan-muted it-scan-cycle-help">
                Each run is one card. Status cards inside show the path in order — click a
                status to open that step.
              </p>
              <div className="it-scan-cycle-cards">
                {scanCycles.map((cycle) => {
                  const active =
                    selectedCycleId === cycle.id ||
                    (selectedCycleId == null && cycle.isCurrent);
                  const stages = stagesForCycle(cycle);
                  return (
                    <article
                      key={cycle.id}
                      className={`it-scan-run-card${active ? ' is-active' : ''}${
                        cycle.isCurrent ? ' is-current' : ' is-past'
                      }`}
                      aria-label={`Run ${cycle.attempt} history`}
                    >
                      <header className="it-scan-run-card-head">
                        <button
                          type="button"
                          className="it-scan-cycle-card-main"
                          onClick={() => openCycle(cycle)}
                        >
                          <div className="it-scan-cycle-card-top">
                            <span className="it-scan-cycle-attempt">Run {cycle.attempt}</span>
                            <span
                              className={`it-scan-status tone-${
                                cycle.isCurrent ? 'active' : 'muted'
                              }`}
                            >
                              {cycleEndLabel(cycle)}
                            </span>
                          </div>
                          <div className="it-scan-cycle-when">
                            {formatWhen(cycle.endedAt || cycle.startedAt)}
                            {cycle.note ? ` · ${cycle.note}` : ''}
                          </div>
                        </button>
                      </header>

                      {stages.length > 0 ? (
                        <ol className="it-scan-status-flow" aria-label={`Run ${cycle.attempt} status path`}>
                          {stages.map((stage, idx) => {
                            const visitActive = selectedStageVisitId === stage.id;
                            const revisit = stages
                              .slice(0, idx)
                              .some((s) => s.node === stage.node);
                            const visitNo = stages.filter(
                              (s, j) => j <= idx && s.node === stage.node
                            ).length;
                            const statusText = blockStatusLabel(stage);
                            const tone = blockStatusTone(stage);
                            return (
                              <li key={stage.id || `${cycle.id}-${idx}`} className="it-scan-status-flow-item">
                                {idx > 0 && (
                                  <span className="it-scan-status-flow-arrow" aria-hidden>
                                    <ArrowRight size={18} strokeWidth={2.25} />
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className={`it-scan-status-card tone-${tone}${
                                    visitActive ? ' is-active' : ''
                                  }${revisit ? ' is-revisit' : ''}`}
                                  onClick={() => openStageVisit(cycle, stage)}
                                  title={
                                    [
                                      stage.label || stage.node,
                                      statusText,
                                      stage.note,
                                      formatWhen(stage.at),
                                    ]
                                      .filter(Boolean)
                                      .join(' · ')
                                  }
                                >
                                  <span className="it-scan-status-card-name">
                                    {stage.label || stage.node}
                                    {revisit ? ` (#${visitNo})` : ''}
                                  </span>
                                  <span className={`it-scan-status tone-${tone}`}>
                                    {statusText}
                                  </span>
                                  {stage.note ? (
                                    <span className="it-scan-status-card-meta">{stage.note}</span>
                                  ) : null}
                                  {stage.at && (
                                    <span className="it-scan-status-card-when">
                                      {formatWhen(stage.at)}
                                    </span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ol>
                      ) : (
                        <p className="it-scan-muted it-scan-cycle-help">
                          No status steps recorded for this run yet.
                        </p>
                      )}
                    </article>
                  );
                })}
              </div>
            </>
          )}
          {browsingPast && (
            <div className="it-scan-cycle-banner" role="status">
              <span>
                <strong>Past run — not the active run.</strong>{' '}
                {viewingHistoricCycle ? (
                  <>
                    Showing history for <strong>Run {selectedCycle.attempt}</strong>
                    {selectedStageVisit
                      ? ` · ${selectedStageVisit.label || selectedStageVisit.node}`
                      : ''}
                    {selectedStageVisit?.note ? ` — ${selectedStageVisit.note}` : ''}.
                  </>
                ) : (
                  <>
                    Showing an earlier{' '}
                    <strong>
                      {selectedStageVisit?.label || selectedStageVisit?.node}
                    </strong>
                    {selectedStageVisit?.note ? ` — ${selectedStageVisit.note}` : ''}{' '}
                    on the current run.
                  </>
                )}
              </span>
              <button type="button" className="garage-btn garage-btn-secondary garage-btn-sm" onClick={showCurrentRun}>
                Show current run
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="it-scan-error">
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </p>
      )}

      <div className="it-scan-layout">
        <aside
          className={`it-scan-rail${viewingHistoricCycle ? ' is-past-run' : ''}`}
          aria-label={
            viewingHistoricCycle
              ? `IT stages for past Run ${selectedCycle?.attempt}`
              : 'IT stages'
          }
        >
          {viewingHistoricCycle && (
            <p className="it-scan-rail-past-label">
              Progress trail · Run {selectedCycle.attempt} (past)
            </p>
          )}
          <ol className="it-scan-v-timeline">
            {visibleNodes.map((n, index) => {
              const meta = nodes[n.id] || {};
              const state = timelineClass(meta.state || 'grey');
              const selected = selectedNode === n.id;
              const historicJob =
                viewingHistoricCycle && selectedCycle
                  ? n.id === 'security'
                    ? selectedCycle.security
                    : n.id === 'codeReview'
                      ? selectedCycle.codeReview
                      : null
                  : null;
              const liveJob =
                n.id === 'security' ? it?.security : n.id === 'codeReview' ? it?.codeReview : null;
              const job = viewingHistoricCycle ? historicJob : liveJob;
              const jobActive = !viewingHistoricCycle && job && ACTIVE.has(job.status);
              const nodeWaiver = viewingHistoricCycle
                ? meta.state === 'approved'
                : n.id === 'security'
                  ? it?.securityWaiver
                  : n.id === 'codeReview'
                    ? it?.codeReviewWaiver
                    : null;
              const grade = meta.grade || job?.grade || null;
              const verdict = meta.verdict || job?.verdict || null;
              return (
                <li key={n.id} className={`it-scan-v-step ${state}${selected ? ' is-selected' : ''}`}>
                  {index < visibleNodes.length - 1 && <span className="it-scan-v-connector" aria-hidden />}
                  <button
                    type="button"
                    className="it-scan-v-btn"
                    onClick={() => {
                      const liveNode = nodeFromSubflow(it?.subflowNode);
                      // Stay on a manually chosen stage across polls; re-follow when
                      // the user clicks the live stage again (or Show current).
                      followActiveStageRef.current =
                        !viewingHistoricCycle && n.id === liveNode;
                      setSelectedNode(n.id);
                      if (viewingHistoricCycle && selectedCycle) {
                        if (n.id === 'security') {
                          setViewJobId(selectedCycle.securityJobId || null);
                        } else if (n.id === 'codeReview') {
                          setViewJobId(selectedCycle.codeReviewJobId || null);
                        } else {
                          setViewJobId(null);
                        }
                      } else {
                        setSelectedStageVisitId(null);
                        setViewJobId(null);
                      }
                    }}
                    aria-pressed={selected}
                  >
                    <span className="it-scan-v-dot" />
                    <span className="it-scan-v-body">
                      <span className="it-scan-v-label">{meta.label || n.label}</span>
                      {nodeWaiver && (
                        <span className="it-scan-v-meta it-scan-v-meta-waived">
                          Blocked · approved
                        </span>
                      )}
                      {jobActive && (
                        <span className="it-scan-v-meta">{Math.max(0, job.progressPct || 0)}%</span>
                      )}
                      {!jobActive && !nodeWaiver && grade && (
                        <span className="it-scan-v-meta">Grade {String(grade)}</span>
                      )}
                      {!jobActive && !nodeWaiver && verdict && (
                        <span className="it-scan-v-meta">{formatVerdict(verdict)}</span>
                      )}
                      {meta.hint && selected && (
                        <span className="it-scan-v-hint">{meta.hint}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <div className="it-scan-pane-wrap" ref={paneWrapRef}>
          {renderPane()}
        </div>
      </div>

      <GrantSecScanAccessModal
        isOpen={showPat}
        repo={{
          id: payload?.secScanRepoId || request?.secScanRepoId,
          displayName: request?.title || 'Origin repository',
          gitUrl: editRepoUrl || it?.repoUrl || request?.repoUrl || payload?.repoUrl,
          accessStatus: it?.accessStatus,
          accessMessage: it?.accessMessage,
        }}
        serviceUser={gitServiceUser}
        onClose={() => setShowPat(false)}
        onDone={(token) =>
          handleGrantAccess(token, {
            repoUrl: (editRepoUrl || '').trim() || undefined,
            originRef: (editOriginRef || 'master').trim() || 'master',
          })
        }
      />

      <DeployedUrlModal
        isOpen={deployedUrlModalOpen}
        initialUrl={
          (request?.appType || '').toLowerCase() === 'gem'
            ? request?.gemUrl || ''
            : request?.webUrl || request?.gemUrl || ''
        }
        initialNote={devNote}
        onClose={() => setDeployedUrlModalOpen(false)}
        onConfirm={confirmAdvanceToRelease}
      />
    </section>
  );
};

export default ItScanFlowPanel;
